import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { useBrandTokens } from '../../../shell/shared';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qPatientFilesByEncounter, type PatientFileRow } from '@helios/data-access';
import { useDermatologyAnalysis } from '../../../hooks/useDermatologyAnalysis';
import { DermatologyAnalysisDrawer } from './DermatologyAnalysisDrawer';

/* ------------------------------------------------------------------ */
/*  DermatologyAiAnalyze (PRD.217 §6, §7.1, §7.2)                     */
/*                                                                    */
/*  Image-selection mode for the encounter image area: clinician      */
/*  picks 1–5 same-lesion images and clicks "Analyze Selected" to     */
/*  kick off the Cloud Function. On success, opens the result drawer  */
/*  and the AI Suggested diagnosis card lands in the Diagnoses tab    */
/*  (rendered separately).                                            */
/* ------------------------------------------------------------------ */

const MIN_IMAGES = 1;
const MAX_IMAGES = 5;
const HELPER_TEXT = 'Select 1–5 images of the same lesion for AI analysis.';

interface Props {
  patientId: string;
  encounterId: string;
}

export function DermatologyAiAnalyze({ patientId, encounterId }: Props) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );

  const images = useMemo(
    () => fileRows.filter((f) => f.kind === 'image' && f.url),
    [fileRows],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resultRunId, setResultRunId] = useState<string | null>(null);

  const { status, error, enabled, analyse, reset } = useDermatologyAnalysis();

  const selectedCount = selected.size;
  const overLimit = selectedCount > MAX_IMAGES;
  const canAnalyze =
    enabled && selectedCount >= MIN_IMAGES && selectedCount <= MAX_IMAGES && status !== 'submitting';

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    reset();
  }, [reset]);

  const handleAnalyze = useCallback(async () => {
    if (!canAnalyze) return;
    const result = await analyse({
      orgId,
      patientId,
      encounterId,
      encounterImageIds: [...selected],
    });
    if (result) {
      setResultRunId(result.analysisRunId);
      setDrawerOpen(true);
      if (result.status === 'completed') {
        setSelected(new Set());
      }
    }
  }, [analyse, canAnalyze, encounterId, orgId, patientId, selected]);

  if (!enabled) {
    return (
      <div style={styles.empty}>
        Dermatology AI is not configured for this environment. Set{' '}
        <code>VITE_DERMATOLOGY_AI_URL</code> to enable.
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div style={styles.empty}>
        No encounter images yet. Upload images from the <strong>Images</strong> view first, then
        return to AI Analyze.
      </div>
    );
  }

  const validationMessage = overLimit
    ? 'You can analyze up to 5 images at a time. Please select images of the same lesion.'
    : null;

  return (
    <div style={styles.wrapper}>
      {/* Helper + counter row */}
      <div style={styles.controls}>
        <div>
          <div style={styles.helper}>{HELPER_TEXT}</div>
          <div style={styles.counter}>
            {selectedCount} selected
            {selectedCount > 0 ? ` · ${MAX_IMAGES - selectedCount} remaining` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="md" onClick={clearSelection} disabled={selectedCount === 0}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleAnalyze}
            disabled={!canAnalyze}
            style={{ minWidth: 160 }}
          >
            {status === 'submitting' ? 'Analyzing…' : 'Analyze Selected'}
          </Button>
        </div>
      </div>

      {(validationMessage || error) && (
        <div style={{ ...styles.errorBanner, ...(overLimit ? {} : styles.errorBannerSubtle) }}>
          {validationMessage ?? error}
        </div>
      )}

      {/* Selectable image grid */}
      <div style={styles.grid}>
        {images.map((file) => {
          const isSelected = selected.has(file.id);
          const disabled = !isSelected && selectedCount >= MAX_IMAGES;
          return (
            <button
              key={file.id}
              type="button"
              onClick={() => toggle(file.id)}
              aria-pressed={isSelected}
              disabled={disabled}
              style={{
                ...styles.imageCard,
                borderColor: isSelected ? primary : 'var(--border, #e5e7eb)',
                boxShadow: isSelected ? `0 0 0 2px ${primary}33` : 'none',
                opacity: disabled ? 0.45 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <img
                src={file.url ?? undefined}
                alt={file.filename ?? ''}
                style={styles.imageImg}
              />
              <span
                style={{
                  ...styles.checkbox,
                  background: isSelected ? primary : 'rgba(255,255,255,0.85)',
                  color: isSelected ? '#fff' : 'var(--text-muted, #6b7280)',
                  borderColor: isSelected ? primary : 'var(--border, #e5e7eb)',
                }}
                aria-hidden
              >
                {isSelected ? <CheckIcon /> : null}
              </span>
              {file.filename && (
                <span style={styles.filenamePill} title={file.filename}>
                  {file.filename}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <DermatologyAnalysisDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        encounterId={encounterId}
        analysisRunId={resultRunId}
      />
    </div>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  wrapper: {
    padding: 'var(--sp-5, 20px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-4, 16px)',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 'var(--sp-4, 16px)',
    flexWrap: 'wrap',
  },
  helper: {
    fontSize: 'var(--font-size-sm, 12px)',
    fontWeight: 500,
    color: 'var(--text, #111827)',
  },
  counter: {
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-muted, #6b7280)',
    marginTop: 2,
  },
  errorBanner: {
    padding: 'var(--sp-3, 12px)',
    borderRadius: 'var(--radius-md, 10px)',
    background: '#fee2e2',
    color: '#991b1b',
    fontSize: 'var(--font-size-sm, 12px)',
  },
  errorBannerSubtle: {
    background: '#fef3c7',
    color: '#92400e',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 'var(--sp-3, 12px)',
  },
  imageCard: {
    position: 'relative',
    border: '2px solid',
    borderRadius: 'var(--radius-md, 10px)',
    padding: 0,
    background: 'var(--bg-surface, #fff)',
    aspectRatio: '1',
    overflow: 'hidden',
    transition: 'box-shadow 150ms, border-color 150ms, opacity 150ms',
  },
  imageImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  checkbox: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1.5px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filenamePill: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    right: 6,
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm, 8px)',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  empty: {
    padding: 'var(--sp-8, 32px) var(--sp-5, 20px)',
    textAlign: 'center',
    color: 'var(--text-muted, #6b7280)',
    fontSize: 'var(--font-size-sm, 12px)',
  },
};
